/**
 * Converts an IST timestamp string to a Date object
 * Handles timestamps with or without timezone info
 * @param dateString - IST timestamp string (may or may not have timezone)
 * @returns Date object or null if invalid
 */
export const parseIstDate = (dateString: string | undefined | null): Date | null => {
  if (!dateString) return null;

  try {
    // If the string doesn't have timezone info (ends with just time, not Z or +/-),
    // assume it's IST (UTC+5:30) and append the timezone offset
    let dateStr = dateString;
    if (!dateStr.includes('Z') && !dateStr.match(/[+-]\d{2}:\d{2}$/)) {
      // Assume IST timezone (UTC+5:30)
      dateStr = dateStr + '+05:30';
    }

    const date = new Date(dateStr);
    if (isNaN(date.getTime())) {
      return null;
    }
    return date;
  } catch (error) {
    console.error('Error parsing IST date:', error, dateString);
    return null;
  }
};

export const parseUtcDate = (dateString: string | undefined | null): Date | null => {
  if (!dateString) return null;

  try {
    const hasTimezone = dateString.includes('Z') || /[+-]\d{2}:\d{2}$/.test(dateString);
    const date = new Date(hasTimezone ? dateString : `${dateString}Z`);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    console.error('Error parsing UTC date:', error, dateString);
    return null;
  }
};

/**
 * Formats a last seen timestamp to a user-friendly string
 * @param lastSeenUtc - IST timestamp string
 * @returns Formatted string or 'Never'
 */
export const formatLastSeen = (lastSeenUtc: string | undefined): string => {
  const lastSeenDate = parseIstDate(lastSeenUtc);
  if (!lastSeenDate) return 'Never';

  try {
    const now = new Date();
    
    const isToday = lastSeenDate.toDateString() === now.toDateString();
    const isThisYear = lastSeenDate.getFullYear() === now.getFullYear();

    const options: Intl.DateTimeFormatOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    };

    if (isToday) {
      return lastSeenDate.toLocaleTimeString('en-IN', options);
    }

    if (isThisYear) {
      return lastSeenDate.toLocaleString('en-IN', {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    }

    return lastSeenDate.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting last seen date:', error);
    return 'Never';
  }
};

/**
 * Formats an IST timestamp to a localized date string
 * @param dateString - IST timestamp string
 * @returns Formatted date string
 */
export const formatIstDate = (dateString: string | undefined | null): string => {
  const date = parseIstDate(dateString);
  if (!date) return '';

  try {
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  } catch (error) {
    console.error('Error formatting IST date:', error);
    return dateString || '';
  }
};

/**
 * Formats an IST timestamp to a localized date-time string
 * @param dateString - IST timestamp string
 * @returns Formatted date-time string
 */
export const formatIstDateTime = (dateString: string | undefined | null): string => {
  const date = parseIstDate(dateString);
  if (!date) return '';

  try {
    return date.toLocaleString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  } catch (error) {
    console.error('Error formatting IST date-time:', error);
    return dateString || '';
  }
};

/**
 * Formats a date for YouTube preview cards with smart date formatting:
 * - Today: just time (e.g., "10:30 am")
 * - Yesterday: date and time (e.g., "18th, 10:30 am")
 * - Last month: month abbreviation, day, time (e.g., "Jan 18, 10:30 am")
 * - Last year: year, month abbreviation, day, time (e.g., "24 Jan 18, 10:30 am")
 * @param dateString - IST timestamp string or UTC timestamp string
 * @returns Formatted date string
 */
export const formatYouTubeDate = (dateString: string | undefined | null): string => {
  if (!dateString) return '';
  
  let date: Date | null = null;
  
  // Try parsing as IST first
  date = parseIstDate(dateString);
  
  // Fallback: try parsing as UTC or standard date string
  if (!date) {
    try {
      date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return dateString; // Return original if can't parse
      }
    } catch {
      return dateString;
    }
  }

  if (!date) return dateString;
  
  return formatYouTubeDateFromDate(date);
};

/**
 * Helper function to format a Date object for YouTube preview cards
 */
const formatYouTubeDateFromDate = (date: Date): string => {
  const now = new Date();
  
  // Get dates in local timezone for comparison
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  // Format time (always shown) - use local time
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });

  // Check if today
  if (dateOnly.getTime() === today.getTime()) {
    return timeStr; // Just time: "10:30 am"
  }

  // Check if yesterday
  if (dateOnly.getTime() === yesterday.getTime()) {
    const day = date.getDate();
    const dayWithSuffix = getDayWithSuffix(day);
    return `${dayWithSuffix}, ${timeStr}`; // "18th, 10:30 am"
  }

  // Check if same year
  if (date.getFullYear() === now.getFullYear()) {
    const month = date.toLocaleString('en-US', { month: 'short' });
    const day = date.getDate();
    return `${month} ${day}, ${timeStr}`; // "Jan 18, 10:30 am"
  }

  // Different year
  const year = date.getFullYear().toString().slice(-2); // Last 2 digits
  const month = date.toLocaleString('en-US', { month: 'short' });
  const day = date.getDate();
  return `${year} ${month} ${day}, ${timeStr}`; // "24 Jan 18, 10:30 am"
};

/**
 * Gets the day with ordinal suffix (1st, 2nd, 3rd, 4th, etc.)
 */
const getDayWithSuffix = (day: number): string => {
  if (day >= 11 && day <= 13) {
    return `${day}th`;
  }
  switch (day % 10) {
    case 1:
      return `${day}st`;
    case 2:
      return `${day}nd`;
    case 3:
      return `${day}rd`;
    default:
      return `${day}th`;
  }
};
